import { useEffect, useRef } from 'react';

const MENU_ID = 'fab-menu';

/**
 * Floating action button with a small menu.
 *
 * Accessibility:
 *  - Trigger has `aria-expanded` + `aria-controls` + dynamic `aria-label`.
 *  - Menu uses `role="menu"` and items use `role="menuitem"`.
 *  - Escape closes the menu and restores focus to the trigger.
 *  - When the menu opens, focus moves to the first menu item for keyboard users.
 */
export default function Fab({ fabOpen, setFabOpen, onAddTx, onImport, theme: t, mobile }) {
  const triggerRef = useRef(null);
  const firstItemRef = useRef(null);

  useEffect(() => {
    if (!fabOpen) return;
    firstItemRef.current?.focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setFabOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [fabOpen, setFabOpen]);

  const handleImport = () => {
    setFabOpen(false);
    onImport();
  };

  const handleAddTx = () => {
    setFabOpen(false);
    onAddTx();
  };

  const itemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: t.cardBg,
    color: t.text,
    border: '1px solid ' + t.cardBorder,
    borderRadius: 28,
    padding: '10px 18px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
    whiteSpace: 'nowrap',
  };

  return (
    <>
      {fabOpen && (
        <div
          onClick={() => setFabOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 24 }}
          aria-hidden="true"
        />
      )}
      <div style={{ position: 'fixed', bottom: mobile ? 20 : 28, right: mobile ? 20 : 28, zIndex: 25, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 12 }}>
        {fabOpen && (
          <div id={MENU_ID} role="menu" aria-label="Quick actions" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 12 }}>
            <button ref={firstItemRef} role="menuitem" onClick={handleImport} style={itemStyle}>
              {'\u{1F4C4}'} Import Statement
            </button>
            <button role="menuitem" onClick={handleAddTx} style={itemStyle}>
              {'\u{1F4B3}'} Add Transaction
            </button>
          </div>
        )}
        <button
          ref={triggerRef}
          onClick={() => setFabOpen(prev => !prev)}
          aria-label={fabOpen ? 'Close quick actions' : 'Open quick actions'}
          aria-haspopup="menu"
          aria-expanded={fabOpen}
          aria-controls={MENU_ID}
          style={{
            width: 56,
            height: 56,
            borderRadius: 28,
            background: 'linear-gradient(135deg,#2563eb,#1d4ed8)',
            color: 'white',
            border: 'none',
            fontSize: 28,
            fontWeight: 300,
            cursor: 'pointer',
            boxShadow: '0 6px 20px rgba(37,99,235,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'transform 0.2s, box-shadow 0.2s',
            lineHeight: 1,
            transform: fabOpen ? 'rotate(45deg)' : 'rotate(0deg)',
          }}
        >+</button>
      </div>
    </>
  );
}
